/**
 * Top-right row of small round menu icons (Character, Talents, Inventory, Gems, Vault, Rank,
 * Meters) with key hints under them and badges (unspent talent points, vault ready). The
 * settings gear (settingsPanel.ts) sits at the far right of the row. Icons and hotkeys share
 * `menuHotkeys.run`, so a click and a key press do exactly the same thing, on the press.
 *
 * Also registers the in-run HUD side of the hotkeys: settings, meters, key hints, hide UI.
 */
import type { HudUpdate } from '../hud';
import { el, setClass } from '../dom';
import { type HudGlyph, type RoundButton, roundButton } from '../kit';
import { metaBridge } from '../../meta/metaBridge';
import { menuBadges } from '../../meta/badges';
import { toggleSettingsPanel } from '../settingsPanel';
import type { HudWidget, HudWidgetContext } from '../widgets';
import { type MenuRoute, commandKeyLabel, menuHotkeys, routeKeyLabel } from './hotkeys';
import { keyHintsRef } from './keyHints';
import { metersRef } from './meters';

interface MenuItem {
  route: MenuRoute;
  label: string;
  glyph: HudGlyph;
}

export const MENU_ITEMS: readonly MenuItem[] = [
  { route: 'character', label: 'Character', glyph: 'character' },
  { route: 'talents', label: 'Waystone Grid', glyph: 'talents' }, // +grid: the N key opens the Waystone Grid
  { route: 'inventory', label: 'Inventory', glyph: 'bag' },
  { route: 'gems', label: 'Gems', glyph: 'gems' },
  { route: 'vault', label: 'Vault', glyph: 'vault' },
  { route: 'rank', label: 'Rank', glyph: 'rank' },
];

const UI_OFF = 'hk-ui-off';

export class MenuBar implements HudWidget {
  readonly root: HTMLElement;
  private readonly buttons: { route: MenuRoute; btn: RoundButton }[] = [];
  private readonly meters: RoundButton;
  private readonly hudRoot: HTMLElement;
  private readonly uiNote: HTMLElement;
  private ended = false;
  private dead = false;
  private readonly unsubscribe: () => void;

  constructor(host: HTMLElement, ctx: Pick<HudWidgetContext, 'root' | 'handlers'>) {
    this.hudRoot = ctx.root;
    const root = (this.root = el('nav', 'hk-menu', host));
    root.setAttribute('aria-label', 'Menu');
    for (const it of MENU_ITEMS) {
      const btn = roundButton(root, {
        icon: it.glyph,
        keybind: routeKeyLabel(it.route),
        label: it.label,
        size: 'sm',
        onPress: () => menuHotkeys.run({ kind: 'route', route: it.route }),
      });
      this.buttons.push({ route: it.route, btn });
    }
    el('span', 'hk-menu-sep', root);
    this.meters = roundButton(root, {
      icon: 'meters',
      keybind: commandKeyLabel('meters'),
      label: 'Meters',
      size: 'sm',
      onPress: () => menuHotkeys.run({ kind: 'meters' }),
    });
    this.uiNote = el('div', 'hk-ui-note', ctx.root, `Interface hidden · ${commandKeyLabel('toggleUi')} to show`);

    // Badges follow the profile (not polled per frame).
    const store = metaBridge.store;
    const sync = (): void => {
      if (!store) return;
      const b = menuBadges(store.profile);
      this.badge('talents', b.talents);
      this.badge('vault', b.vault);
      // +grid: the star pulses until the new grid points are spent.
      this.buttons.find((x) => x.route === 'talents')?.btn.root.classList.toggle('hk-grid-pulse', b.talents > 0);
    };
    sync();
    this.unsubscribe = store ? store.subscribe(sync) : () => {};

    menuHotkeys.hud = {
      active: () => !this.ended,
      settings: () => toggleSettingsPanel(),
      meters: () => metersRef.current?.toggle(),
      hints: () => keyHintsRef.current?.toggle(),
      toggleUi: () => this.toggleUi(),
      dead: () => this.dead,
      release: () => ctx.handlers.release?.(),
    };
  }

  private badge(route: MenuRoute, v: number | boolean): void {
    this.buttons.find((b) => b.route === route)?.btn.setBadge(v);
  }

  toggleUi(force?: boolean): void {
    const off = force ?? !this.hudRoot.classList.contains(UI_OFF);
    setClass(this.hudRoot, UI_OFF, off);
  }

  update(h: HudUpdate): void {
    const phase = h.world.mplus.phase;
    const ended = phase === 'completed' || phase === 'depleted';
    if (ended && !this.ended) metaBridge.closeOverlay();
    this.ended = ended;
    const me = h.playerId ? h.world.units[h.playerId] : undefined;
    this.dead = !!me && !me.alive;
    const open = metaBridge.overlayRoute();
    for (let i = 0; i < this.buttons.length; i++) {
      const b = this.buttons[i]!;
      b.btn.setActive(b.route === open || (b.route === 'talents' && open === 'grid')); // +grid: talents → grid alias
    }
    const m = metersRef.current;
    this.meters.setActive(!!m && !m.isCollapsed);
  }

  dispose(): void {
    this.unsubscribe();
    metaBridge.closeOverlay();
    menuHotkeys.hud = null;
    this.uiNote.remove();
    this.hudRoot.classList.remove(UI_OFF);
  }
}
